(ns ui.view.transaction-process.cards.decisions
  (:require
   [common.localization :refer [tr]]
   [common.schema.entities.transaction-process :as tp-schema]
   [common.transaction-process.resolver :as resolver]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]
   [ui.view.transaction-process.cards.card-header :as card-header]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.modals.decision.event :as decision-event]
   [ui.view.transaction-process.sub :as sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- requiredness-options []
  (tp-util/schema-opts->input-opts tp-schema/needed-options))

(defn- decision-status-badge
  [decision]
  (let [draft? (:decision/draft? decision)]
    [tp-util/status-badge {:label   (if draft? (tr :draft) (tr :logged))
                           :variant (if draft? :warning :success)}]))

(defn- requiredness-badge
  "Tri-state badge. nil (unselected) renders nothing so an undecided row is not
   misrepresented as a settled Not needed."
  [required?]
  (case required?
    true  [tp-util/status-badge {:label (tr :required) :variant :success}]
    false [tp-util/status-badge {:label (tr :not-needed) :variant :neutral}]
    nil   nil))

(defn- requiredness-select
  [{:keys [value disabled? on-change]}]
  [:div {:class "w-40"}
   [labelled-input/view
    {:data-type   :data.type/keyword
     :value       value
     :placeholder (tr :select-placeholder)
     :options     (requiredness-options)
     :disabled?   disabled?
     :on-change   (when-not disabled? on-change)}]])

(defn requiredness-control-kind
  [{:keys [editable? can-write?]}]
  (if (and can-write? editable?) :select :badge))

(defn requiredness-input-value
  "Return the persisted needed value when explicitly set; otherwise map the
  slot's resolved template default back to the selector's Needed/Not needed
  keyword. This changes presentation only: an unset field remains nil in the
  database."
  [{:keys [entry lookup]}]
  (let [{:keys [from-field true-value false-value]} (:slot/required entry)
        stored-value (get lookup from-field)]
    (if (some? stored-value)
      stored-value
      (case (tp-util/resolve-required? {:entry entry :lookup lookup})
        true  true-value
        false false-value
        nil))))

(defn decision-log-blocked?
  [entry danger-attestations?]
  (boolean (and (:slot/block-on-danger-attestation? entry)
                danger-attestations?)))

(defn display-decision-slot?
  "A template may hide an explicitly Not needed decision. Preserve an existing
   decision instance so historical data is never concealed."
  [{:keys [entry instance]} lookup]
  (or (some? instance)
      (not (and (:slot/hide-when-not-needed? entry)
                (false? (tp-util/resolve-required? {:entry  entry
                                                    :lookup lookup}))))))

(defn- review-decision-row
  [{:keys [label needed-value required? editable? can-write? on-change]}]
  [:div {:class "border border-gray-200 rounded-lg p-4 flex items-center justify-between"}
   [:span {:class "text-sm m-0 text-gray-900 font-medium"} label]
   (case (requiredness-control-kind {:editable? editable?
                                     :can-write? can-write?})
     :select
     [requiredness-select
      {:value     needed-value
       :on-change on-change}]

     :badge
     [requiredness-badge required?])])

(defn- approval-decision-row
  [{:keys [label decision required? can-write? log-blocked? on-log on-edit on-expand]}]
  (let [draft? (and decision (:decision/draft? decision))
        logged? (and decision (not draft?))]
    [:div {:class (uu/cm "border rounded-lg p-4 flex items-center justify-between"
                         (when logged? "cursor-pointer hover:bg-gray-50")
                         (cond
                           logged?         "border-primary-regular/30 bg-primary-regular/5"
                           draft?          "border-yellow-200 bg-yellow-50/50"
                           (not required?) "border-gray-200 opacity-60"
                           :else           "border-gray-200"))
           :on-click (when logged? on-expand)}
     [:div {:class "flex items-center gap-3"}
      [:span {:class "text-sm m-0 text-gray-900 font-medium"}
       label]
      (cond
        decision           [decision-status-badge decision]
        (true? required?)  [tp-util/status-badge {:label (tr :pending) :variant :neutral}]
        (false? required?) [requiredness-badge false]
        :else              [requiredness-badge nil])]
     [:div {:class "flex items-center gap-2"}
      (cond
        logged?
        [:svg {:class "w-4 h-4 text-gray-400"
               :xmlns "http://www.w3.org/2000/svg"
               :viewBox "0 0 24 24"
               :fill "none"
               :stroke "currentColor"
               :stroke-width "2"
               :stroke-linecap "round"
               :stroke-linejoin "round"}
         [:polyline {:points "9 6 15 12 9 18"}]]

        (not required?)
        nil

        (and draft? can-write?)
        [button/sm-secondary-hollow
         {:disabled? log-blocked?
          :tooltip   (when log-blocked?
                       (tr :transaction-process/pm-decision-blocked-by-danger-check))
          :on-click  (fn [e]
                       (dg/stop-propagation e)
                       (on-edit))}
         (tr :transaction-process/log-decision)]

        can-write?
        [button/sm-secondary-hollow
         {:disabled? log-blocked?
          :tooltip   (when log-blocked?
                       (tr :transaction-process/pm-decision-blocked-by-danger-check))
          :on-click  (fn [e]
                       (dg/stop-propagation e)
                       (on-log))}
         (tr :transaction-process/log-decision)])]]))

(defn view
  [{:keys [card ctx]}]
  (let [matched @(rf/subscribe [::sub/matched-decisions ctx])
        matched-attestations @(rf/subscribe [::sub/matched-attestations ctx])
        danger-attestations? (boolean (some resolver/danger-attestation? matched-attestations))
        process @(rf/subscribe [::sub/process ctx])
        lookup process
        stage-config @(rf/subscribe [::sub/stage-config ctx])
        can-write-decisions? @(rf/subscribe [::sub/can-write-field? ctx :decision/date])
        default-participants @(rf/subscribe [::sub/default-decision-participant-ids ctx])
        config-mode? (boolean (:stage/decision-config? stage-config))
        by-slot (->> matched
                     (map (fn [m] [(:slot-kw m) m]))
                     (into {}))
        slots (:card/slots card)]
    [:div {:class "bg-white rounded-lg border border-gray-200 p-6"}
     [card-header/view {:card card}]
     [:div {:class "flex flex-col gap-2"}
      (doall
       (for [slot-kw slots
             :let [{:keys [entry instance]} (get by-slot slot-kw)]
             :when (and entry
                        (display-decision-slot? {:entry    entry
                                                 :instance instance}
                                                lookup))]
         (let [label        (:slot/label entry)
               entity-type  (:slot/entity-type entry)
               needed-field (get-in entry [:slot/required :from-field])
               required?    (tp-util/resolve-required? {:entry entry :lookup lookup})
               editable?    (tp-util/requiredness-editable? entry)
               can-edit?    @(rf/subscribe [::sub/can-write-field? ctx needed-field])]
           ^{:key slot-kw}
           [:div
            (if config-mode?
              [review-decision-row
               {:label          label
                :needed-value   (requiredness-input-value {:entry  entry
                                                           :lookup lookup})
                :required?      required?
                :editable?      editable?
                :can-write?     can-edit?
                :on-change      (fn [new-needed-value]
                                  (rf/dispatch [::event/update-draft-field ctx
                                                needed-field new-needed-value]))}]
              [approval-decision-row
               {:label          label
                :decision       instance
                :required?      required?
                :can-write?     can-write-decisions?
                :log-blocked?   (decision-log-blocked? entry danger-attestations?)
                :on-log         (fn []
                                  (rf/dispatch [::decision-event/open ctx
                                                {:entity-type             entity-type
                                                 :slot-kw                 slot-kw
                                                 :decision-type-label     label
                                                 :default-participant-ids (get default-participants slot-kw [])}]))
                :on-edit        (fn []
                                  (rf/dispatch [::decision-event/open ctx
                                                {:entity-type          entity-type
                                                 :slot-kw              slot-kw
                                                 :decision-type-label  label
                                                 :existing-decision    instance}]))
                :on-expand      (fn []
                                  (rf/dispatch [::event/set-detail-panel ctx
                                                {:panel-type :decision
                                                 :item       {:slot-kw         slot-kw
                                                              :entry           entry
                                                              :decision-entity instance
                                                              :label           label}}]))}])])))]]))
